/**
 * Drives the anime bowl's LED scoreboards from the live match state.
 *
 * The export replaced 24 frozen FONT objects with ONE quad per board (north +
 * south), both sharing the emissive material `GX_Scoreboard` — its README says
 * exactly how to drive them: swap the emissive map for a CanvasTexture and
 * redraw, using the UV layout table it documents. Both boards share the one
 * material, so one canvas feeds both, and the south quad's UVs are mirrored in
 * the GEOMETRY so the same image reads correctly from its side of the bowl.
 *
 * Region maths: the README's v-ranges are Blender-style (v=0 at the BOTTOM),
 * while a canvas draws y-down and glTF UVs run v-down. The texture is created
 * with `flipY = false` (matching how GLTFLoader delivers every texture the
 * material was authored against), so canvas y = (1 − v) × H places each region.
 * Get this wrong and the clock paints above the score, upside down.
 *
 * Repaints only when a VALUE changes — feeding it every frame costs nothing
 * (same contract as the procedural board this replaces, context.ts §feed).
 */
import {
  CanvasTexture, LinearFilter, NearestFilter, SRGBColorSpace,
  type Mesh, type MeshStandardMaterial, type Object3D,
} from 'three';
import type { RenderState } from '../context.js';

/** Matches the baked SCOREBOARD_DYNAMIC image (1024×140, per the export). */
const W = 1024;
const H = 140;

/** README UV table, Blender v-convention. */
const REGION = {
  homeFlag: { u0: 0.023, u1: 0.107, v0: 0.31, v1: 0.73 },
  homeName: { u0: 0.12, u1: 0.30, v0: 0.41, v1: 0.64 },
  score: { u0: 0.39, u1: 0.61, v0: 0.37, v1: 0.76 },
  timer: { u0: 0.40, u1: 0.60, v0: 0.08, v1: 0.30 },
  awayName: { u0: 0.71, u1: 0.89, v0: 0.41, v1: 0.64 },
  awayFlag: { u0: 0.893, u1: 0.977, v0: 0.31, v1: 0.73 },
} as const;

type Box = { x: number; y: number; w: number; h: number };
function px(r: { u0: number; u1: number; v0: number; v1: number }): Box {
  return { x: r.u0 * W, y: (1 - r.v1) * H, w: (r.u1 - r.u0) * W, h: (r.v1 - r.v0) * H };
}

export class ScoreboardSystem {
  private canvas: HTMLCanvasElement | null = null;
  private tex: CanvasTexture | null = null;
  private last = { home: '', away: '', a: -1, b: -1, mm: -1, ss: -1, phase: '' };

  /** Find `GX_Scoreboard` under the mounted module and take over its emissive. */
  bind(module: Object3D): boolean {
    let mat: MeshStandardMaterial | null = null;
    module.traverse((n) => {
      const mesh = n as Mesh;
      if (!(mesh as unknown as { isMesh?: boolean }).isMesh) return;
      const list = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const m of list) if (/scoreboard/i.test(m.name)) mat = m as MeshStandardMaterial;
    });
    if (!mat) {
      console.warn('[Flagship Stadium] scoreboard material not found — boards stay static');
      return false;
    }
    this.canvas = document.createElement('canvas');
    this.canvas.width = W;
    this.canvas.height = H;
    this.tex = new CanvasTexture(this.canvas);
    this.tex.flipY = false; // glTF UV convention — see the header
    this.tex.colorSpace = SRGBColorSpace;
    this.tex.magFilter = NearestFilter; // keeps the LED look (export README)
    this.tex.minFilter = LinearFilter;
    this.tex.generateMipmaps = false;
    const m = mat as MeshStandardMaterial;
    m.emissiveMap?.dispose();
    m.emissiveMap = this.tex;
    m.emissive.setRGB(1, 1, 1);
    m.needsUpdate = true;
    this.paint('HOME', 'AWAY', 0, 0, 0, 0, '');
    return true;
  }

  /** Feed every frame; repaints only when a value changes. */
  update(state: RenderState): void {
    if (!this.tex) return;
    const sb = state.scoreboard;
    const home = sb.show ? sb.homeName : 'HOME';
    const away = sb.show ? sb.awayName : 'AWAY';
    const a = sb.show ? sb.scoreA : 0;
    const b = sb.show ? sb.scoreB : 0;
    const clock = sb.show ? Math.max(0, Math.floor(sb.clockS)) : 0;
    const mm = Math.floor(clock / 60);
    const ss = clock % 60;
    const phase = sb.show ? sb.phase : '';
    const L = this.last;
    if (L.home === home && L.away === away && L.a === a && L.b === b && L.mm === mm && L.ss === ss && L.phase === phase) return;
    this.paint(home, away, a, b, mm, ss, phase);
  }

  private paint(home: string, away: string, a: number, b: number, mm: number, ss: number, phase: string): void {
    const g = this.canvas?.getContext('2d');
    if (!g || !this.tex) return;
    this.last = { home, away, a, b, mm, ss, phase };

    // Near-black LED panel.
    g.fillStyle = '#0a0e17';
    g.fillRect(0, 0, W, H);

    const text = (s: string, box: Box, color: string, weight = 700): void => {
      const size = Math.floor(box.h * 0.9);
      g.font = `${weight} ${size}px "Arial Narrow", Arial, sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillStyle = color;
      g.fillText(s.toUpperCase(), box.x + box.w / 2, box.y + box.h / 2 + 1, box.w);
    };
    const flag = (box: Box, hex: string): void => {
      g.fillStyle = hex;
      g.fillRect(box.x, box.y, box.w, box.h);
      g.strokeStyle = 'rgba(255,255,255,0.5)';
      g.lineWidth = 2;
      g.strokeRect(box.x, box.y, box.w, box.h);
    };

    flag(px(REGION.homeFlag), '#9e2630'); // club colours — same muted palette as the flags
    flag(px(REGION.awayFlag), '#27538f');
    text(home, px(REGION.homeName), '#f2f4f8');
    text(away, px(REGION.awayName), '#f2f4f8');
    text(`${a} - ${b}`, px(REGION.score), '#ffd34d');
    // Clock shares its row with the phase tag when one is set ("34:12 · 1ST").
    const t = `${mm}:${String(ss).padStart(2, '0')}${phase ? ` · ${phase}` : ''}`;
    text(t, px(REGION.timer), '#8fe08f', 600);

    this.tex.needsUpdate = true;
  }

  dispose(): void {
    this.tex?.dispose();
    this.tex = null;
    this.canvas = null;
  }
}
